import { useEffect, useState, useCallback, useRef } from "react";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import "open-iconic/font/css/open-iconic-bootstrap.css";
import { SortingState, IntegratedSorting } from "@devexpress/dx-react-grid";
import {
  Grid,
  TableHeaderRow,
  Table,
  TableColumnResizing,
} from "@devexpress/dx-react-grid-bootstrap4";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { GetLicenseManagementCustomers } from "../../actions/superUserQueries";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import { CreateNewScormLicenseLicenses } from "../../actions/superUserMutations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WarnLimitForLicMgt } from "../../constants/ConstantVars";
import { injectIntl } from "react-intl";

function LicMgtLicensesTable({
  customer,
  toggleUpdate,
  handleUpdateData,
  intl,
}) {
  const [licenseRows, setLicenseRows] = useState([]);

  const itemsRef = useRef([]);
  const { mutate } = useMutation(CreateNewScormLicenseLicenses);

  const queryClient = useQueryClient();
  const licenseCustomers = useQuery(
    "FetchLicenseManagementCustomers",
    GetLicenseManagementCustomers()
  );

  const handleAddLicenseForCourse = useCallback(
    (courseId, i) => {
      if (
        typeof itemsRef.current[i] === "object" ||
        itemsRef.current[i].length < 1
      ) {
        toast.warn(intl.formatMessage({ id: "YouMustFillInNumberOfLicenses" }));
      } else {
        mutate(
          {
            courseId: courseId,
            customerId: customer.customer.id,
            quantity: itemsRef.current[i],
          },
          {
            onSuccess: (data) => {
              if (data && data.status > 199 && data.status < 300) {
                toast.success(<FormattedMessage id="LicenseAdded" />);
                let tempCustomerData = [...licenseCustomers.data];
                let itemIndex = tempCustomerData.findIndex(
                  (cust) => cust.customer.id === customer.customer.id
                );
                tempCustomerData[itemIndex].licensesPerCourse =
                  data.data.result.licensesPerCourse;
                queryClient.setQueryData(
                  ["FetchLicenseManagementCustomers"],
                  tempCustomerData
                );
                handleUpdateData();
              } else {
                toast.warn(data.response.data);
              }
            },
            throwOnError: true,
            onError: (err) => {
              toast.warn(err);
            },
          }
        );
      }
    },
    [
      customer.customer.id,
      licenseCustomers.data,
      handleUpdateData,
      queryClient,
      mutate,
      intl,
    ]
  );

  const addLicensesCol = useCallback(
    (courseId, i) => {
      return (
        <>
          <Input
            type="number"
            placeholder={intl.formatMessage({ id: "NumberOfLicenses" })}
            ref={(el) => (itemsRef.current[i] = el)}
            onChange={(e) => {
              itemsRef.current[i] = e.target.value;
            }}
          />
          <Button
            outline
            color="primary"
            onClick={() => handleAddLicenseForCourse(courseId, i)}
          >
            <FormattedMessage id="AddLicenses" />
          </Button>
        </>
      );
    },
    [handleAddLicenseForCourse, intl]
  );

  const getStatusIcon = useCallback((nbrOfLicensesNotUsed) => {
    if (nbrOfLicensesNotUsed === 0) {
      return (
        <FontAwesomeIcon
          icon="exclamation-triangle"
          className={"text-danger"}
        />
      );
    } else if (nbrOfLicensesNotUsed < WarnLimitForLicMgt + 1) {
      return (
        <FontAwesomeIcon icon="exclamation-triangle" className="text-warning" />
      );
    } else {
      return <FontAwesomeIcon icon="thumbs-up" className="text-success" />;
    }
  }, []);

  //Behöver hämta från query här istället för att få rätt på customer troligtvis.
  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(
      0,
      licenseCustomers.data.find(
        (liccust) => liccust.customer.id === customer.customer.id
      ).licensesPerCourse
    );
    const licenseManagementTableDataSource = [];
    licenseCustomers.data
      .find((liccust) => liccust.customer.id === customer.customer.id)
      .licensesPerCourse.forEach((lic, i) => {
        let student = {
          uniqueId: lic.course.id,
          name: lic.course.name,
          nbrOfLicenses: lic.nbrOfLicenses,
          nbrOfValidLicenses: lic.nbrOfValidLicenses,
          nbrOfLicensesNotUsed: lic.nbrOfLicensesNotUsed,
          created: moment(lic.course.created).format("L"),
          administer: addLicensesCol(lic.course.id, i),
          status: getStatusIcon(lic.nbrOfLicensesNotUsed),
        };
        licenseManagementTableDataSource.push(student);
      });
    setLicenseRows(licenseManagementTableDataSource);
  }, [
    licenseCustomers.data,
    toggleUpdate,
    customer.customer.id,
    addLicensesCol,
    getStatusIcon,
  ]);

  let sizingExtensions = [
    { columnName: "name", width: 150 },
    { columnName: "nbrOfLicenses", width: 170 },
    { columnName: "nbrOfValidLicenses", width: 200 },
    { columnName: "nbrOfLicensesNotUsed", width: 220 },
    { columnName: "created", width: 120 },
    { columnName: "administer", width: 150 },
    { columnName: "status", width: 90 },
  ];

  const licenseManagementTableColumns = [
    { name: "name", title: <FormattedMessage id="Name" /> },
    {
      name: "nbrOfLicenses",
      title: <FormattedMessage id="NumberOfLicenses" />,
    },
    {
      name: "nbrOfValidLicenses",
      title: <FormattedMessage id="NumberOfValidLicenses" />,
    },
    {
      name: "nbrOfLicensesNotUsed",
      title: <FormattedMessage id="NumberOfUnusedLicenses" />,
    },
    { name: "created", title: <FormattedMessage id="Created" /> },
    { name: "administer", title: <FormattedMessage id="Administer" /> },
    { name: "status", title: <FormattedMessage id="Status" /> },
  ];

  const TableComponent = ({ ...restProps }) => (
    <Table.Table {...restProps} className="table-striped" />
  );

  const getRowId = (row) => row.uniqueId;

  return (
    <Grid
      rows={licenseRows}
      columns={licenseManagementTableColumns}
      getRowId={getRowId}
    >
      <SortingState />
      <IntegratedSorting />
      <Table
        noDataRowComponent={() => (
          <tr>
            <td colSpan="7" className="text-center bg-white">
              <big className="text-muted">
                <FormattedMessage id="NoLicenses" />
              </big>
            </td>
          </tr>
        )}
        striped={true}
        tableComponent={TableComponent}
      />
      <TableColumnResizing defaultColumnWidths={sizingExtensions} />

      <TableHeaderRow showSortingControls />
    </Grid>
  );
}

export default injectIntl(LicMgtLicensesTable);
